import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

import { useAuth } from '../auth/AuthContext';
import { LiveSignalWall } from '../components/LiveSignalWall';
import { LiveSessionTile } from '../components/LiveSessionTile';
import { listMyLiveSessions, type LiveSessionSummary } from '../api/live';
import { listMyLiveCases, type MyLiveCase } from '../api/liveWall';

/**
 * The Live page for a participant (LIVE-WALL-PARTICIPANT): the case they are
 * recording in right now, with the whole team overlaid on shared axes — the
 * same wall the tenant admin watches, read-only. Only cases they are
 * streaming into appear; nothing else in their tenant does. Their own name
 * opens their full live detail, as "Watch live" on the dashboard does.
 */
export function MyLiveWall() {
  const { user } = useAuth();
  const me = user?.id ?? null;
  const [cases, setCases] = useState<MyLiveCase[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mySessions, setMySessions] = useState<LiveSessionSummary[]>([]);
  const [drill, setDrill] = useState<LiveSessionSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      void listMyLiveCases()
        .then((cs) => {
          if (!cancelled) {
            setCases(cs);
            setError(null);
          }
        })
        .catch(() => {
          if (!cancelled) setError('Failed to load your live cases.');
        });
      void listMyLiveSessions()
        .then((ss) => {
          if (!cancelled) setMySessions(ss);
        })
        .catch(() => undefined);
    };
    load();
    const t = setInterval(load, 3000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const myLiveSession = useMemo(() => mySessions[0] ?? null, [mySessions]);

  const label = (c: MyLiveCase) => (id: string) =>
    id === me ? 'You' : (c.participant_names[id] ?? 'Participant');

  return (
    <Stack spacing={3}>
      <Stack>
        <Typography variant="h4">Live Recording</Typography>
        <Typography variant="body2" color="text.secondary">
          The case you&rsquo;re recording in right now — everyone on your team
          overlaid on shared axes, with a marker the instant your signals
          diverge.
        </Typography>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {cases === null ? (
        <CircularProgress />
      ) : cases.length === 0 ? (
        <Alert severity="info">
          You aren&rsquo;t recording right now. Once a capture device starts a
          session for you, your case appears here within a few seconds.
        </Alert>
      ) : (
        <Stack spacing={4} divider={<Divider flexItem />}>
          {cases.map((c) => {
            const name = label(c);
            const isSplit = !!c.split_of;
            return (
              <Stack key={c.case_id} spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
                  <Typography variant="h6">{c.procedure_name ?? 'Procedure'}</Typography>
                  <Chip
                    size="small"
                    color={isSplit ? 'default' : 'success'}
                    variant={isSplit ? 'outlined' : 'filled'}
                    label={isSplit ? 'Own track' : `${c.participant_ids.length} live`}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {c.tenant_name}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {c.participant_ids.map((pid) => (
                    <Chip
                      key={pid}
                      size="small"
                      variant={pid === me ? 'filled' : 'outlined'}
                      color={pid === me ? 'primary' : 'default'}
                      label={name(pid)}
                      onClick={
                        pid === me && myLiveSession
                          ? () => setDrill(myLiveSession)
                          : undefined
                      }
                    />
                  ))}
                </Stack>
                <LiveSignalWall
                  tenantId={null}
                  caseId={c.case_id}
                  participantLabel={name}
                  onParticipantClick={(pid) => {
                    if (pid === me && myLiveSession) setDrill(myLiveSession);
                  }}
                />
              </Stack>
            );
          })}
        </Stack>
      )}

      <Dialog open={drill !== null} onClose={() => setDrill(null)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          Your live detail
          <IconButton onClick={() => setDrill(null)} size="small" aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {drill && (
            <LiveSessionTile
              key={drill.session_id}
              scope={{ mine: true }}
              session={drill}
              variant="solo"
              onEnded={() => setDrill(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
